<script lang="ts" setup>
import { ref } from 'vue'

import EventEditActionButtons from '@/pages/CreateEventProcessingPage/ui/layout/EventEditActionButtons.vue'
import { IEventEdit } from '@/enitites/event'
import { useSetPaymentSystem } from '@/modules/Payment/api/queries'
import { TSetPaymentSystemPayload } from '@/modules/Payment/types'
import { ModalWrapper } from '@/shared/ui/modals'
import IconTier from '@/features/tickets/assets/IconTier.vue'

const { mutate: setPaymentSystem, isPending, error } = useSetPaymentSystem()

const props = defineProps<{
  eventData: IEventEdit
}>()

const emit = defineEmits<{
  (e: 'next'): void
  (e: 'back'): void
  (e: 'cancel'): void
}>()

const isPaymentSystemEnabled = ref<boolean>(!!props.eventData.paymentSystem?.isEnabled)
const isShowLearnMore = ref(false)

function saveEventData(onSuccess: () => void) {
  if (isPending.value) return

  const payload: TSetPaymentSystemPayload = isPaymentSystemEnabled.value
    ? {
        isEnabled: true,
        isRefundable: props.eventData.paymentSystem?.isRefundable || false,
        refundableUntil: props.eventData.paymentSystem?.refundableUntil || '',
        selectedBankId: props.eventData.paymentSystem?.selectedBankId,
      }
    : {
        isEnabled: false,
        priceRange: props.eventData.paymentSystem?.priceRange || '',
        purchaseTicketsExternalUrl: props.eventData.paymentSystem?.purchaseTicketsExternalUrl || '',
      }

  setPaymentSystem(
    {
      eventId: props.eventData.id,
      payload,
    },
    { onSuccess }
  )
}

function handleNext() {
  saveEventData(() => {
    emit('next')
  })
}

function handleCancel() {
  saveEventData(() => {
    emit('cancel')
  })
}
</script>

<template>
  <div class="payment-mode pb-10">
    <div class="title-container">
      <p class="form-title">Break Konnect Payment System</p>
      <v-btn
        color="var(--sky-blue)"
        class="learn-more-btn"
        text
        @click="isShowLearnMore = true"
      >
        Learn more
      </v-btn>
    </div>

    <v-radio-group
      v-model="isPaymentSystemEnabled"
      class="payment-options"
    >
      <v-radio
        label="Sell tickets through the secure Break Konnect system"
        :value="true"
        color="var(--color-blue-800)"
        class="payment-option"
      />
      <v-radio
        label="No thanks"
        :value="false"
        color="var(--color-blue-800)"
        class="payment-option"
      />
    </v-radio-group>

    <EventEditActionButtons
      :is-loading="isPending"
      :disabled="false"
      :error="error?.message"
      @next="handleNext"
      @back="$emit('back')"
      @cancel="handleCancel"
    />

    <ModalWrapper
      :is-show.sync="isShowLearnMore"
      modal-width="500"
    >
      <div class="learn-more-content">
        <IconTier
          size="60"
          class="icon"
        />
        <div class="text-container">
          <h2 class="title">Streamline your events</h2>
          <div class="description">
            <b>Features</b><br />
            Use features like:<br />
            <ol>
              <li>Our QR code scanner to check people in</li>
              <li>Track payments through our ticket summary for each event</li>
              <li>
                Have the ability to assign tickets to profiles or send tickets to users without a profile via email.
              </li>
              <li>Create early bird discounts and discount codes to all or selected ticket tiers.</li>
              <li>Create ticket tiers based on battles</li>
            </ol>
          </div>
          <div class="description">
            <b>Fees</b><br />
            There is a $2 service fee charge per ticket. This is one of the ways that Break Konnect monetizes. We made
            sure to be competitive with other platforms like Eventbrite, who charge 3.7% + $1.79 per ticket.
          </div>
        </div>
      </div>
    </ModalWrapper>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/style/mixins';

.payment-mode {
  max-width: 410px;
  margin: 140px auto 60px;

  .title-container {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 12px;
  }

  .form-title {
    margin-bottom: 0;
    color: var(--color-dark-800);
    font-weight: 700;
    font-size: 20px;
    line-height: 26px;
  }

  .learn-more-btn {
    padding: 4px;
    height: auto;
    min-height: 0;
    font-weight: 700;
    font-size: 16px;
    line-height: 24px;
    text-transform: none;
  }

  .payment-options {
    margin-top: 0;
    padding-top: 0;

    .payment-option {
      margin-bottom: 12px;
      padding: 16px 0;
    }
  }
}

.learn-more-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  padding: 40px;

  .icon {
    margin-bottom: 20px;
  }

  .text-container {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .title {
      font-size: 24px;
      line-height: 30px;
      font-weight: 700;
      color: var(--color-dark-800);
    }

    .description {
      font-size: 16px;
      line-height: 24px;
      color: var(--color-dark-800);

      b {
        font-weight: 700;
      }
    }
  }
}
</style>
